import React, { useEffect, useState } from "react";
import Responsive from "react-responsive-decorator";
import { Map, LngLat, Marker, supported } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import inactiveMapMarker from "../assets/inactive-map-marker.svg";
import { sendTealiumEvent, CONSTANTS } from "../helpers";
import { Pharmacy, PharmacyGeoPoint, PharmacyGeoPoints } from "../@types";

interface PharmacySelectionMapProps {
  data: {
    sourceAddress: PharmacyGeoPoint;
    pharmacies: PharmacyGeoPoints;
    geoError: string;
  };
  page: number;
  selectedPharmacies: Pharmacy[];
}

const PharmacySelectionMap: React.FunctionComponent<PharmacySelectionMapProps> = props => {
  const [mapMarkers, setMapMarkers] = useState<Marker[]>([]);

  const updateMapMarkers = (): void => {
    mapMarkers.forEach(marker => {
      const element = marker.getElement();
      const npi = element.getAttribute("data-npi");
      if (npi && props.selectedPharmacies.map(p => p.npi).includes(npi)) {
        element.classList.add("PharmacySelectionMap__marker--active");
      } else {
        element.classList.remove("PharmacySelectionMap__marker--active");
      }
    });
  };

  useEffect(() => {
    if (supported()) {
      const map = new Map({
        container: "PharmacySelectionMap",
        style: "https://maps.cms.gov/pages/streets/streets.json",
      });

      map.getCanvas().setAttribute("tabindex", "-1");

      let startHoverTime = 0;

      map.on("mouseover", (): void => {
        startHoverTime = Date.now();
      });

      map.on("mouseout", (): void => {
        const timeSpentHovering = (Date.now() - startHoverTime) / 1000;

        sendTealiumEvent(
          "Find Plans - Add Pharmacy",
          `Map Hover - ${timeSpentHovering} seconds`
        );
      });

      map.on("load", () => {
        Array.from(
          document.querySelectorAll(".mapboxgl-control-container a")
        ).forEach(a => {
          a.setAttribute("tabindex", "-1");
        });

        const { features: pharmacyPoints } = props.data.pharmacies;
        const [
          sourceLng,
          sourceLat,
        ] = props.data.sourceAddress.geometry.coordinates;

        if (pharmacyPoints.length) {
          map.addSource("pharmacies", {
            type: "geojson",
            data: props.data.pharmacies,
          });

          const lats: number[] = [];
          const longs: number[] = [];
          const markers: Marker[] = [];
          let farthestPharmacy = { lng: 0, lat: 0, distance_miles: 0 };

          pharmacyPoints.forEach((feature, index) => {
            const { geometry, properties } = feature;
            const { coordinates } = geometry as { coordinates: number[] };
            const { npi, distance_miles } = properties as Pharmacy;

            longs.push(coordinates[0]);
            lats.push(coordinates[1]);

            if (distance_miles > farthestPharmacy.distance_miles) {
              farthestPharmacy = {
                lng: coordinates[0],
                lat: coordinates[1],
                distance_miles,
              };
            }

            const pharmacyNumber =
              props.page * CONSTANTS.pharmacySelection.pageCount + (index + 1);

            const markerElement = document.createElement("div");
            markerElement.innerHTML = pharmacyNumber.toString();
            markerElement.className = "PharmacySelectionMap__marker";
            markerElement.setAttribute("data-npi", npi);
            markerElement.style.backgroundImage = `url(${inactiveMapMarker})`;
            markerElement.onclick = (): void => {
              const pharmacyListItem = document.getElementById(npi);
              const pharmacyNumber = pharmacyListItem?.querySelector(
                ".SelectPharmaciesPage__pharmacyNumber"
              )?.textContent;

              pharmacyListItem?.scrollIntoView({ behavior: "smooth" });
              sendTealiumEvent(
                "Find Plans - Add Pharmacy",
                `Map Click: Pharmacy #${(pharmacyNumber && pharmacyNumber[0]) ||
                  0}`
              );
            };

            const marker = new Marker(markerElement)
              .setLngLat(new LngLat(coordinates[0], coordinates[1]))
              .addTo(map);

            markers.push(marker);
          });

          setMapMarkers(markers);

          if (lats.length && longs.length) {
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLong = Math.min(...longs);
            const maxLong = Math.max(...longs);

            map.fitBounds(
              [
                [minLong - 0.01, minLat - 0.01],
                [maxLong + 0.01, maxLat + 0.01],
              ],
              { animate: false }
            );

            const metersToPixelsAtMaxZoom = (
              meters: number,
              latitude: number
            ): number => meters / 0.05 / Math.cos((latitude * Math.PI) / 180);

            const srcLngLat = new LngLat(sourceLng, sourceLat);
            const farthestLngLat = new LngLat(
              farthestPharmacy.lng,
              farthestPharmacy.lat
            );
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const distanceBetween = srcLngLat.distanceTo(farthestLngLat);

            const distanceInPixels = metersToPixelsAtMaxZoom(
              distanceBetween,
              farthestPharmacy.lat
            );

            map.addLayer({
              id: "pharmacy-circle",
              source: {
                type: "geojson",
                data: {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: [sourceLng, sourceLat],
                  },
                  properties: {},
                },
              },
              type: "circle",
              paint: {
                "circle-radius": {
                  stops: [
                    [0, 0],
                    [20, distanceInPixels],
                  ],
                  base: 2,
                },
                "circle-color": "#146A5D",
                "circle-stroke-color": "#146A5D",
                "circle-stroke-width": 1,
                "circle-opacity": 0.1,
                "circle-pitch-scale": "viewport",
              },
            });
          }
        }

        map.resize();
      });
    }
  }, [props.data]);

  useEffect(() => {
    updateMapMarkers();
  }, [props.selectedPharmacies, mapMarkers]);

  return (
    <div id="PharmacySelectionMap" className="PharmacySelectionMap__map" />
  );
};

export default Responsive(PharmacySelectionMap);
